import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useObs } from '../../contexts/ObsContext';
import { ProgressBar, Btn, Spinner } from '../../components/ui';
import { obsAPI } from '../../api';
import useRequireObs from '../../lib/useRequireObs';

const STEPS = ['Context', 'Evidence', 'Summary', 'Feedback'];

/** One observer's note, as written. */
function NoteCard({ note, tone }) {
  return (
    <div className={`rec-note rec-note-${tone}`}>
      <div className="rec-note-head">
        <span className="rec-observer">{note.observer}</span>
        <span className="rec-bucket">{note.bucket_display}</span>
      </div>
      <p className="rec-evidence">{note.evidence_text}</p>
      {note.impact_text && <p className="rec-impact">{note.impact_text}</p>}
    </div>
  );
}

/**
 * What the lead decides before the debrief.
 *
 * A duplicate is noise — the teacher should not hear the same finding twice.
 * A divergence is signal — it marks a moment the framework reads two ways,
 * and how it is resolved is kept as a training label.
 */
export default function ReconcilePage() {
  const navigate = useNavigate();
  const { obs } = useObs();
  useRequireObs();

  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy]       = useState('');

  const load = useCallback(() => {
    if (!obs?.id) return;
    setLoading(true);
    obsAPI.reconcile(obs.id)
      .then(({ data }) => setData(data))
      .catch(err => {
        console.error(err);
        toast.error(err?.response?.data?.detail || 'Could not load the notes to reconcile.');
      })
      .finally(() => setLoading(false));
  }, [obs?.id]);

  useEffect(load, [load]);

  const merge = async (keepId, mergeId) => {
    setBusy(mergeId);
    try {
      await obsAPI.mergeNote(mergeId, keepId);
      toast.success('Merged. The teacher will hear it once.');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not merge those notes.');
    } finally { setBusy(''); }
  };

  const resolve = async (id, resolution) => {
    setBusy(id);
    try {
      await obsAPI.resolveDivergence(id, resolution);
      toast.success('Recorded.');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not record that.');
    } finally { setBusy(''); }
  };

  const isLead = data?.is_lead;
  const nothingToDo = data && data.duplicates.length === 0 && data.unresolved_divergences === 0;

  return (
    <div className="screen">
      <div className="obsbar">
        <button className="obsbar-back" onClick={() => navigate('/observe/evidence')}>← Back</button>
        <div className="obsbar-title">Reconcile</div>
      </div>
      <ProgressBar steps={STEPS} current={1} />

      <div className="scroll">
        <div className="pad">
          {loading && (
            <div style={{ display:'flex', justifyContent:'center', padding:'48px 0' }}><Spinner /></div>
          )}

          {!loading && data && (
            <>
              <div className="adm-note">
                {data.observer_count} observers recorded this lesson independently.
                {isLead
                  ? ' As lead, you decide what the teacher hears.'
                  : ' The lead observer resolves these.'}
              </div>

              {nothingToDo && (
                <div className="dash-empty">
                  Nothing to reconcile — no repeated findings and no disagreements
                  about which bucket a moment belongs in.
                </div>
              )}

              {/* ── Duplicates ─────────────────────────────────────────── */}
              {data.duplicates.length > 0 && (
                <>
                  <div className="rec-sec">Possible duplicates</div>
                  <div className="co-hint" style={{ marginBottom: 10 }}>
                    Two observers appear to have written down the same moment.
                  </div>

                  {data.duplicates.map((d, i) => (
                    <div key={i} className="rec-card">
                      <div className="rec-card-head">
                        <span className="rec-conf">{d.confidence}% alike</span>
                        <span className="rec-reason">{d.reason}</span>
                      </div>
                      <NoteCard note={d.note_a} tone="a" />
                      <NoteCard note={d.note_b} tone="b" />
                      {isLead && (
                        <div className="rec-actions">
                          <button className="rec-btn" disabled={!!busy}
                                  onClick={() => merge(d.note_a.id, d.note_b.id)}>
                            Keep {d.note_a.observer.split(' ')[0]}'s
                          </button>
                          <button className="rec-btn" disabled={!!busy}
                                  onClick={() => merge(d.note_b.id, d.note_a.id)}>
                            Keep {d.note_b.observer.split(' ')[0]}'s
                          </button>
                          <button className="rec-btn ghost" disabled={!!busy}
                                  onClick={() => toast.info('Both kept — they will both be said.')}>
                            Keep both
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </>
              )}

              {/* ── Divergences ────────────────────────────────────────── */}
              {data.divergences.length > 0 && (
                <>
                  <div className="rec-sec">Read differently</div>
                  <div className="co-hint" style={{ marginBottom: 10 }}>
                    The same moment, tagged to different buckets. Worth talking about
                    in the debrief — this is where the framework is ambiguous.
                  </div>

                  {data.divergences.map(d => (
                    <div key={d.id} className={`rec-card${d.resolved ? ' resolved' : ''}`}>
                      <div className="rec-card-head">
                        <span className="rec-conf neg">{d.note_a.bucket_display} vs {d.note_b.bucket_display}</span>
                        {d.resolved && <span className="rec-reason">Resolved · {d.resolution.replace(/_/g, ' ')}</span>}
                      </div>
                      <NoteCard note={d.note_a} tone="a" />
                      <NoteCard note={d.note_b} tone="b" />

                      {isLead && !d.resolved && (
                        <div className="rec-actions">
                          <button className="rec-btn" disabled={!!busy}
                                  onClick={() => resolve(d.id, 'chose_a')}>
                            {d.note_a.bucket_display}
                          </button>
                          <button className="rec-btn" disabled={!!busy}
                                  onClick={() => resolve(d.id, 'chose_b')}>
                            {d.note_b.bucket_display}
                          </button>
                          <button className="rec-btn ghost" disabled={!!busy}
                                  onClick={() => resolve(d.id, 'kept_both')}>
                            Both are right
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </>
              )}
            </>
          )}

          <Btn variant="blue" onClick={() => navigate('/observe/conclusions')} style={{ marginTop: 14 }}>
            Continue to summary →
          </Btn>
        </div>
      </div>
    </div>
  );
}
